import dayjs from "dayjs"
import en from "dayjs/locale/en"
import relativeTime from "dayjs/plugin/relativeTime"
import updateLocale from "dayjs/plugin/updateLocale"

// dayjs types the relative-time strings without the week keys its thresholds accept.
type RelativeTimeStrings = ILocale["relativeTime"] & { w: string; ww: string }

// The stock scale jumps from days straight to months and rounds up ("45 minutes" reads as
// "an hour"); ours steps through weeks and floors, so a value only moves to the next unit
// once a whole one has passed.
dayjs.extend(relativeTime, {
    rounding: Math.floor,
    thresholds: [
        { l: "s", r: 59, d: "second" },
        { l: "m", r: 1 },
        { l: "mm", r: 59, d: "minute" },
        { l: "h", r: 1 },
        { l: "hh", r: 23, d: "hour" },
        { l: "d", r: 1 },
        { l: "dd", r: 6, d: "day" },
        { l: "w", r: 1 },
        { l: "ww", r: 4, d: "week" },
        { l: "M", r: 1 },
        { l: "MM", r: 11, d: "month" },
        { l: "y", r: 1 },
        { l: "yy", d: "year" },
    ],
})
dayjs.extend(updateLocale)

// The plugin installs the English relative-time strings on the locale; only weeks are missing.
const englishRelativeTime: RelativeTimeStrings = { ...dayjs.Ls.en.relativeTime, w: "a week", ww: "%d weeks" }
dayjs.updateLocale("en", { relativeTime: englishRelativeTime })

// Abbreviated variant for compact surfaces ("30min ago", "2w ago", "Just now").
const SHORT_LOCALE = "en-short"
const JUST_NOW = "Just now"
const shortRelativeTime: RelativeTimeStrings = {
    future: "in %s",
    past: "%s ago",
    s: JUST_NOW,
    m: "1min",
    mm: "%dmin",
    h: "1h",
    hh: "%dh",
    d: "1d",
    dd: "%dd",
    w: "1w",
    ww: "%dw",
    M: "1m",
    MM: "%dm",
    y: "1y",
    yy: "%dy",
}
dayjs.locale(SHORT_LOCALE, { ...en, name: SHORT_LOCALE, relativeTime: shortRelativeTime }, true)

// e.g. "in 2 weeks", "3 days ago".
export function formatRelativeTime(date: string | Date): string {
    return dayjs().to(date)
}

// e.g. "30min ago", "2w ago", "Just now".
export function formatRelativeTimeShort(date: string | Date): string {
    // Suffix by hand so "Just now" doesn't become "Just now ago".
    const value = dayjs(date).locale(SHORT_LOCALE).fromNow(true)
    return value === JUST_NOW ? value : `${value} ago`
}
